import { motion } from "framer-motion";
import { ArrowLeft, Phone, MapPin, Clock, Star, Scissors, Instagram, Facebook, Mail, Award, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ProjectTopBar from "@/components/ProjectTopBar";
import Seo from "@/components/Seo";

export default function SalonCote() {
  const navigate = useNavigate(); const setLocation = (p) => navigate(p);
  const [currentPage, setCurrentPage] = useState("home");

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      name: "Coupe Femme",
      price: "55€",
      duration: "45 min",
      desc: "Coupe personnalisée avec shampoing premium et brushing professionnel. Analyse morphologique complète."
    },
    {
      name: "Coupe Homme",
      price: "35€",
      duration: "30 min",
      desc: "Coupe moderne ou classique avec finition au rasoir. Style adapté à votre personnalité."
    },
    {
      name: "Coloration Complète",
      price: "95€",
      duration: "90 min",
      desc: "Coloration avec produits professionnels L'Oréal Professionnel. Test d'allergie inclus."
    },
    {
      name: "Balayage",
      price: "130€",
      duration: "120 min",
      desc: "Technique de coloration subtile pour un effet naturel et lumineux. Effet soleil garanti."
    },
    {
      name: "Soin Kératine",
      price: "75€",
      duration: "60 min",
      desc: "Soin profond réparateur pour cheveux abîmés. Brillance et douceur immédiates."
    },
    {
      name: "Brushing Premium",
      price: "40€",
      duration: "30 min",
      desc: "Mise en forme professionnelle avec produits thermiques protecteurs."
    },
  ];

  const galleryImages = [
    "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&h=400&fit=crop&q=80",
    "https://images.unsplash.com/photo-1582095133179-bfd08e2fc6b3?w=600&h=400&fit=crop&q=80",
    "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=600&h=400&fit=crop&q=80",
    "https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&h=400&fit=crop&q=80",
    "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=600&h=400&fit=crop&q=80",
    "https://images.unsplash.com/photo-1595476108010-b4d1f102b1b1?w=600&h=400&fit=crop&q=80",
  ];

  const tarifs = {
    coupes: [
      { service: "Coupe Femme", price: "55€" },
      { service: "Coupe Homme", price: "35€" },
      { service: "Coupe Enfant (-12 ans)", price: "25€" },
      { service: "Coupe + Barbe", price: "45€" },
    ],
    colorations: [
      { service: "Coloration Simple", price: "95€" },
      { service: "Balayage", price: "130€" },
      { service: "Mèches", price: "105€" },
      { service: "Ombré Hair", price: "120€" },
    ],
    soins: [
      { service: "Soin Kératine", price: "75€" },
      { service: "Brushing", price: "40€" },
      { service: "Lissage Brésilien", price: "280€" },
      { service: "Botox Capillaire", price: "90€" },
    ],
  };

  return (
    <>
      <Seo title="Salon Côté — concept coiffure" description="Exemple de site pour un salon de coiffure : prestations, prise de rendez-vous et galerie. Concept ML. Agence Web." />
      <ProjectTopBar />
      <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="bg-black border-b border-[#D4AF37]/30 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center">
                <Scissors className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-2xl font-serif font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                  SALON CÔTÉ
                </h1>
                <p className="text-xs text-gray-400 tracking-widest">PARIS • COIFFURE D'EXCELLENCE</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-8">
              {[
                { id: "home", label: "Accueil" },
                { id: "services", label: "Services" },
                { id: "galerie", label: "Galerie" },
                { id: "tarifs", label: "Tarifs" },
                { id: "reservation", label: "Réservation" },
                { id: "contact", label: "Contact" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => handlePageChange(item.id )}
                  className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                    currentPage === item.id 
                      ? "text-[#D4AF37]" 
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* HOME PAGE */}
      {currentPage === "home" && (
        <>
          {/* Hero */}
          <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1920&h=1080&fit=crop&q=80')] bg-cover bg-center opacity-20" />
            
            {/* Overlay pattern */}
            <div className="absolute inset-0" style={{
              backgroundImage: `repeating-linear-gradient(90deg, transparent 0, transparent 50px, rgba(212, 175, 55, 0.03) 50px, rgba(212, 175, 55, 0.03) 51px)`
            }} />

            <div className="max-w-7xl mx-auto px-6 py-32 relative z-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
              >
                <div className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black px-6 py-2 rounded-full text-sm font-bold mb-8 tracking-wider">
                  ✨ EXCELLENCE DEPUIS 1995
                </div>
                <h1 className="text-4xl md:text-9xl font-serif font-bold mb-8 leading-tight">
                  <span className="block text-white">L'Art de la</span>
                  <span className="block bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] text-transparent bg-clip-text">
                    Coiffure
                  </span>
                </h1>
                <p className="text-2xl text-gray-300 mb-12 max-w-2xl mx-auto">
                  Révélez votre beauté naturelle avec nos experts parisiens
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:opacity-90 text-black text-xl font-bold px-12 h-16 rounded-full transition-all hover:scale-105"
                    onClick={() => handlePageChange("reservation")}
                  >
                    Prendre Rendez-vous
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline" 
                    className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-xl font-bold px-12 h-16 rounded-full transition-all"
                    onClick={() => handlePageChange("services")}
                  >
                    Nos Services
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-3xl mx-auto mt-20">
                  {[
                    { number: "28+", label: "Ans d'Excellence" },
                    { number: "2000+", label: "Clients Satisfaits" },
                    { number: "5★", label: "Note Moyenne" },
                  ].map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className="text-5xl font-black bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text mb-2">
                        {stat.number}
                      </div>
                      <div className="text-sm text-gray-400 uppercase tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* About Section */}
          <section className="py-32 px-6 bg-white text-black">
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl md:text-6xl font-serif font-bold text-black mb-6">
                  Qui Sommes-Nous ?
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mb-8" />
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Depuis 1995, **Salon Côté** incarne l'excellence capillaire parisienne. 
                  Notre équipe de coiffeurs passionnés et diplômés met son savoir-faire 
                  au service de votre beauté.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  Spécialisés dans les coupes tendances, colorations sur-mesure et soins 
                  haute qualité, nous créons le look qui vous ressemble dans un cadre 
                  luxueux et raffiné.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { icon: <Award className="w-8 h-8" />, title: "Expertise", desc: "28 ans d'expérience" },
                    { icon: <CheckCircle className="w-8 h-8" />, title: "Qualité", desc: "Produits premium" },
                    { icon: <Star className="w-8 h-8" />, title: "Excellence", desc: "5★ sur Google" },
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center text-black">
                        {item.icon}
                      </div>
                      <p className="font-bold text-black text-sm">{item.title}</p>
                      <p className="text-xs text-gray-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[3/4] relative rounded-3xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&h=1000&fit=crop&q=80" 
                    alt="Salon" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
                <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center text-black">
                  <div className="text-center">
                    <div className="text-4xl font-black">98%</div>
                    <div className="text-xs font-bold">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Services Preview */}
          <section className="py-20 px-6 bg-black">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-5xl font-serif font-bold text-center mb-16">
                <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                  Nos Prestations
                </span>
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  { icon: "✂️", title: "Coupe", desc: "Femme • Homme" },
                  { icon: "🎨", title: "Coloration", desc: "Balayage • Ombré" },
                  { icon: "✨", title: "Soins", desc: "Kératine • Botox" },
                  { icon: "💆", title: "Spa Capillaire", desc: "Détente absolue" },
                ].map((item, i) => (
                  <Card 
                    key={i} 
                    className="p-8 text-center bg-gradient-to-br from-gray-900 to-black border-2 border-[#D4AF37]/20 hover:border-[#D4AF37] transition-all cursor-pointer group"
                    onClick={() => handlePageChange("services")}
                  >
                    <div className="text-4xl md:text-6xl mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                    <h3 className="font-serif text-2xl text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-32 px-6 bg-white text-black">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-5xl font-serif font-bold text-center mb-4">
                Nos Clientes Témoignent
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-16" />
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { name: "Sophie Martin", text: "Une équipe professionnelle et à l'écoute. Je suis cliente depuis 10 ans et chaque visite est un moment de pur bonheur !", rating: 5 },
                  { name: "Marc Laurent", text: "Excellent salon, coupe parfaite à chaque fois. L'ambiance est agréable et le résultat toujours au rendez-vous.", rating: 5 },
                  { name: "Julie Rousseau", text: "Ma coloriste est une vraie artiste ! Elle a su trouver exactement la nuance que je voulais. Je recommande vivement.", rating: 5 },
                ].map((review, i) => (
                  <Card key={i} className="p-8 bg-black text-white border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all">
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, j) => (
                        <Star key={j} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                    <p className="text-gray-300 mb-6 italic leading-relaxed">"{review.text}"</p>
                    <p className="font-bold text-[#D4AF37]">{review.name}</p>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* SERVICES PAGE */}
      {currentPage === "services" && (
        <div className="py-32 px-6 bg-black">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                Nos Services
              </span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-20" />
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, i) => (
                <Card key={i} className="p-8 bg-gradient-to-br from-gray-900 to-black border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] hover:shadow-2xl transition-all group">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-serif font-bold text-white group-hover:text-[#D4AF37] transition-colors">{service.name}</h3>
                    <div className="text-right">
                      <p className="text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">{service.price}</p>
                      <p className="text-sm text-gray-400">{service.duration}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">{service.desc}</p>
                  <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:opacity-90 text-black font-bold">
                    Réserver
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GALERIE PAGE */}
      {currentPage === "galerie" && (
        <div className="py-32 px-6 bg-black">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                Notre Galerie
              </span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-20" />
            <div className="grid md:grid-cols-3 gap-6">
              {galleryImages.map((img, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl overflow-hidden group cursor-pointer border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all">
                  <img 
                    src={img} 
                    alt={`Gallery ${i + 1}`} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TARIFS PAGE */}
      {currentPage === "tarifs" && (
        <div className="py-32 px-6 bg-black">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                Nos Tarifs
              </span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-20" />
            
            <div className="space-y-16">
              {Object.entries(tarifs).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-3xl font-serif font-bold text-white mb-8 pb-4 border-b-2 border-[#D4AF37]">
                    {category === 'coupes' ? 'Coupes' : category === 'colorations' ? 'Colorations' : 'Soins'}
                  </h3>
                  <div className="space-y-4">
                    {items.map((item, i) => (
                      <div key={i} className="flex items-center py-4 border-b border-gray-800">
                        <span className="text-xl text-white flex-1">{item.service}</span>
                        <div className="flex-1 mx-6 border-b-2 border-dotted border-gray-700" />
                        <span className="text-2xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 p-12 border-4 border-[#D4AF37] rounded-2xl bg-gradient-to-br from-gray-900 to-black">
              <h3 className="text-3xl font-serif font-bold text-white mb-6 text-center">Forfaits Exclusifs</h3>
              <div className="space-y-4 text-center">
                <p className="text-2xl text-white">
                  Coupe + Coloration: <span className="font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">135€</span>
                </p>
                <p className="text-2xl text-white">
                  Coupe + Soin + Brushing: <span className="font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">110€</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RESERVATION PAGE */}
      {currentPage === "reservation" && (
        <div className="py-32 px-6 bg-black">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                Réservation
              </span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-12" />
            <p className="text-center text-gray-400 mb-16 text-xl">
              Prenez rendez-vous en ligne ou appelez-nous
            </p>

            <Card className="p-12 bg-gradient-to-br from-gray-900 to-black border-2 border-[#D4AF37]/30">
              <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-bold mb-2">Nom *</label>
                    <Input className="bg-black border-2 border-gray-700 focus:border-[#D4AF37] text-white" />
                  </div>
                  <div>
                    <label className="block text-white font-bold mb-2">Prénom *</label>
                    <Input className="bg-black border-2 border-gray-700 focus:border-[#D4AF37] text-white" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-bold mb-2">Téléphone *</label>
                    <Input type="tel" className="bg-black border-2 border-gray-700 focus:border-[#D4AF37] text-white" />
                  </div>
                  <div>
                    <label className="block text-white font-bold mb-2">Email *</label>
                    <Input type="email" className="bg-black border-2 border-gray-700 focus:border-[#D4AF37] text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-white font-bold mb-2">Service souhaité *</label>
                  <select className="w-full px-4 py-3 bg-black border-2 border-gray-700 focus:border-[#D4AF37] rounded-md text-white focus:outline-none">
                    <option>Coupe Femme</option>
                    <option>Coupe Homme</option>
                    <option>Coloration</option>
                    <option>Balayage</option>
                    <option>Soin Kératine</option>
                    <option>Brushing</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-bold mb-2">Date souhaitée *</label>
                    <Input type="date" className="bg-black border-2 border-gray-700 focus:border-[#D4AF37] text-white" />
                  </div>
                  <div>
                    <label className="block text-white font-bold mb-2">Heure *</label>
                    <select className="w-full px-4 py-3 bg-black border-2 border-gray-700 focus:border-[#D4AF37] rounded-md text-white focus:outline-none">
                      <option>09:00</option>
                      <option>10:00</option>
                      <option>11:00</option>
                      <option>14:00</option>
                      <option>15:00</option>
                      <option>16:00</option>
                      <option>17:00</option>
                      <option>18:00</option>
                    </select>
                  </div>
                </div>
                <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:opacity-90 text-black py-6 text-xl font-bold">
                  Confirmer la Réservation
                </Button>
              </form>
            </Card>
          </div>
        </div>
      )}

      {/* CONTACT PAGE */}
      {currentPage === "contact" && (
        <div className="py-32 px-6 bg-black">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
                Nous Contacter
              </span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-20" />

            <div className="grid md:grid-cols-2 gap-12">
              {/* Info */}
              <div className="space-y-8">
                <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border-2 border-[#D4AF37]/30">
                  <h3 className="text-2xl font-bold text-white mb-6">Coordonnées</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <Phone className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-white">Téléphone</p>
                        <p className="text-gray-300 text-lg">01 02 03 25 26</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Mail className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-white">Email</p>
                        <p className="text-gray-300">contact@saloncote.fr</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-white">Adresse</p>
                        <p className="text-gray-300">15 Avenue des Champs-Élysées</p>
                        <p className="text-gray-300">75008 Paris</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Clock className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-white">Horaires</p>
                        <p className="text-gray-300">Mardi - Vendredi: 9h - 19h</p>
                        <p className="text-gray-300">Samedi: 9h - 18h</p>
                        <p className="text-gray-300">Dimanche - Lundi: Fermé</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <div className="flex gap-4">
                  <a href="#" className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center text-black hover:scale-110 transition-transform">
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a href="#" className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center text-black hover:scale-110 transition-transform">
                    <Facebook className="w-6 h-6" />
                  </a>
                </div>
              </div>

              {/* Map */}
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.2888445122494!2d2.3065!3d48.8698!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66fec70fb1a3f%3A0x40b82c3688c9460!2s15%20Avenue%20des%20Champs-%C3%89lys%C3%A9es%2C%2075008%20Paris!5e0!3m2!1sfr!2sfr!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Final */}
      <section className="py-32 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0, transparent 50px, rgba(212, 175, 55, 0.05) 50px, rgba(212, 175, 55, 0.05) 51px)`
        }} />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-serif font-bold mb-8">
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-transparent bg-clip-text">
              Prêt à Sublimer<br />Votre Look ?
            </span>
          </h2>
          <p className="text-2xl text-gray-300 mb-12">
            Prenez rendez-vous dès maintenant avec nos experts
          </p>
          <Button size="lg" className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:opacity-90 text-black text-2xl font-black px-16 h-20 rounded-full hover:scale-105 transition-all">
            <Phone className="w-8 h-8 mr-3" />
            01 02 03 25 26
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-[#D4AF37]/30 text-center">
        <p className="text-gray-400">
          © 2024 Salon Côté. Made with <span className="text-[#D4AF37]">✂️</span> by ML
        </p>
      </footer>
    </div>
    </>
  );
}
